"use client"
import { Text } from "@/elements/Text"
import { TextVariant } from "@/theme"
import { DialogTitle } from "@radix-ui/react-dialog"
import clsx from "clsx"
import { forwardRef, useLayoutEffect } from "react"

export type DialogVariant = "progress" | "warning" | "acknowledgement" | "passive" | "default"

export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  subtitle?: string
  variant?: DialogVariant
  ref?: React.Ref<HTMLDivElement>
  onHeightChange?: (height: number) => void
}

const getTitleProps = (variant: DialogHeaderProps["variant"]): { variant: TextVariant; className: string } => {
  let textVariant: TextVariant
  let className: string
  switch (variant) {
    case "progress":
      textVariant = "body2"
      className = "text-fg-2"
      break
    case "warning":
    case "acknowledgement":
      textVariant = "h5"
      className = "text-fg-1"
      break
    case "passive":
    case "default":
    default:
      textVariant = "CTALarge"
      className = "text-fg-1"
      break
  }
  return { variant: textVariant, className }
}

const getSubtitleProps = (variant: DialogHeaderProps["variant"]): { variant: TextVariant; className: string } => {
  let textVariant: TextVariant
  let className: string
  switch (variant) {
    case "progress":
      textVariant = "h5"
      className = "text-fg-1"
      break
    case "passive":
    case "warning":
    case "acknowledgement":
    case "default":
    default:
      textVariant = "body2"
      className = "text-fg-2"
      break
  }
  return { variant: textVariant, className }
}

export const DialogHeader = forwardRef<HTMLDivElement, DialogHeaderProps>(
  ({ variant = "default", title, subtitle, onHeightChange }, ref) => {
    const titleProps = getTitleProps(variant)
    const subtitleProps = getSubtitleProps(variant)

    useLayoutEffect(() => {
      if (!ref || typeof ref === "function") return

      const element = ref.current
      if (!element || !onHeightChange) return

      const updateHeight = () => {
        const height = element.offsetHeight
        if (height > 0) {
          onHeightChange(height)
        }
      }

      // Initial measurement
      updateHeight()

      // Observe size changes
      const resizeObserver = new ResizeObserver(updateHeight)
      resizeObserver.observe(element)

      return () => {
        resizeObserver.unobserve(element)
      }
    }, [ref, onHeightChange])

    const shouldRenderHeader = title || subtitle

    if (!shouldRenderHeader) return null

    return (
      <div ref={ref} className={"flex h-fit flex-col px-8 pb-6 pt-8"}>
        <div className={clsx("flex flex-col pr-16", variant === "progress" ? "gap-y-2" : "gap-y-4")}>
          <DialogTitle>
            <Text {...titleProps}>{title}</Text>
          </DialogTitle>
          {subtitle && <Text {...subtitleProps}>{subtitle}</Text>}
        </div>
      </div>
    )
  }
)

DialogHeader.displayName = "DialogHeader"
